import z from "zod";
import { EDocumentVerificationStatus } from "../constants";

export const EJoiningFeesPaymentType = {
  UPFRONT: "UPFRONT",
  INSTALLMENT: "INSTALLMENT",
} as const;
export type EJoiningFeesPaymentType = (typeof EJoiningFeesPaymentType)[keyof typeof EJoiningFeesPaymentType];

export enum EJoiningFeesPaymentVendor {
  CASHFREE = "CASHFREE",
  EXTERNAL = "EXTERNAL",
}

/** Admin onboarding API `currentStage` values (walker / partner flow) */
export const EAdminOnboardingCurrentStage = {
  PROFILE: "PROFILE",
  WORK_SETTINGS: "WORK_SETTINGS",
  TRAINING: "TRAINING",
  JOINING_FEES: "JOINING_FEES",
  DOCUMENTS: "DOCUMENTS",
  TERMS: "TERMS",
  COMPLETED: "COMPLETED",
} as const;
export type EAdminOnboardingCurrentStage =
  (typeof EAdminOnboardingCurrentStage)[keyof typeof EAdminOnboardingCurrentStage];

const groomingKitPhotosSchema = z.object({
  dryerPhotoUrl: z.string().nullish(),
  trimmerPhotoUrl: z.string().nullish(),
  scissorsPhotoUrl: z.string().nullish(),
  shampooPhotoUrl: z.string().nullish(),
  kitAdditionalPhotoUrl1: z.string().nullish(),
  kitAdditionalPhotoUrl2: z.string().nullish(),
});

const groomingBrandNamesSchema = z.object({
  kitBrand: z.string().nullish(),
  shampooBrand: z.string().nullish(),
});

const groomingTrainingSchema = z.object({
  experiencePhotos: z.array(z.string()),
  kitPhotos: groomingKitPhotosSchema,
  brandNames: groomingBrandNamesSchema,
  verificationStatus: z.nativeEnum(EDocumentVerificationStatus).nullish(),
});

const walkingTrainingSchema = z.object({
  data: z.record(z.unknown()),
  verificationStatus: z.string().nullish(),
});

const trainingSchema = z.object({
  walking: walkingTrainingSchema.nullish(),
  grooming: groomingTrainingSchema.nullish(),
});

const deliveryAddressSchema = z.object({
  houseNumber: z.string().nullish(),
  floor: z.string().nullish(),
  society: z.string().nullish(),
  area: z.string().nullish(),
  city: z.string().nullish(),
  landmark: z.string().nullish(),
  state: z.string().nullish(),
  pincode: z.string().nullish(),
});

const joiningFeeSchema = z.object({
  paymentType: z.string().nullish(),
  paymentVendor: z.nativeEnum(EJoiningFeesPaymentVendor).nullish(),
  totalAmount: z.number().nullish(),
  installmentAmount: z.number().nullish(),
  orderId: z.string().nullish(),
  orderAmount: z.number().nullish(),
  paymentReference: z.string().nullish(),
  tshirtSize: z.string().nullish(),
  deliveryAddress: deliveryAddressSchema.nullish(),
  paymentMethod: z.string().nullish(),
  paidAt: z.number().nullish(),
  /** Partner order lifecycle status (e.g. PENDING, PAID) */
  orderStatus: z.string().nullish(),
});

const panSchema = z.object({
  panNumber: z.string().nullish(),
  verificationStatus: z.nativeEnum(EDocumentVerificationStatus).nullish(),
});

const bankSchema = z.object({
  accountName: z.string().nullish(),
  accountNumber: z.string().nullish(),
  bankName: z.string().nullish(),
  ifscCode: z.string().nullish(),
  verificationStatus: z.nativeEnum(EDocumentVerificationStatus).nullish(),
});

const emergencyContactSchema = z.object({
  name: z.string().nullish(),
  relation: z.string().nullish(),
  phoneNumber: z.string().nullish(),
});

const documentsSchema = z.object({
  pan: panSchema.nullish(),
  bank: bankSchema.nullish(),
  selfieUrl: z.string().nullish(),
  emergencyContact: emergencyContactSchema.nullish(),
});

export const adminOnboardingDetailsResponseSchema = z.object({
  partnerId: z.string(),
  serviceCategories: z.array(z.string()),
  flowVersion: z.string().nullable(),
  currentStage: z.string().nullable(),
  currentStep: z.string().nullable(),
  training: trainingSchema,
  joiningFee: joiningFeeSchema,
  documents: documentsSchema,
});

export type IAdminOnboardingDetailsResponse = z.infer<typeof adminOnboardingDetailsResponseSchema>;
export type IGroomingTraining = z.infer<typeof groomingTrainingSchema>;
export type IWalkingTraining = z.infer<typeof walkingTrainingSchema>;
export type IJoiningFeeDetails = z.infer<typeof joiningFeeSchema>;
export type IDeliveryAddress = z.infer<typeof deliveryAddressSchema>;
